import { createRouter, createWebHistory } from "vue-router";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import Index from "@/views/HomeView.vue";

// Keep only the Home shell in the initial bundle. Every other page is loaded
// on demand so a first-time visitor does not download dashboard/editor code.
const DashboardLayout = () => import("@/layouts/DashboardLayout.vue");
const NotFound = () => import("@/views/NotFoundView.vue");
const Maintance = () => import("@/views/MaintanceView.vue");
const Login = () => import("@/views/LoginView.vue");
const Register = () => import("@/views/RegisterView.vue");
const Plans = () => import("@/views/PlansView.vue");
const Applicants = () => import("@/views/applicants/ApplicantsView.vue");
const Applicant = () => import("@/views/applicants/ApplicantProfileView.vue");
const ResetPassword = () => import("@/views/reset-password/TokenView.vue");
const Companies = () => import("@/views/companies/CompaniesView.vue");
const CompanyPublicProfile = () => import("@/views/companies/CompanyProfileView.vue");
const Jobs = () => import("@/views/jobs/JobView.vue");
const About = () => import("@/views/AboutView.vue");
const Faq = () => import("@/views/FaqView.vue");
const PrivacyPolicy = () => import("@/views/PrivacyPolicyView.vue");
const Terms = () => import("@/views/TermsView.vue");
const StatusSupport = () => import("@/views/StatusSupportView.vue");
const Compatibility = () => import("@/views/CompatibilityView.vue");
const JobDetailPageComponent = () => import("@/components/Job/JobDetailPageComponent.vue");

const DashboardCompany = () => import("@/views/dashboard/company/IndexView.vue");
const DashboardCompanyPostjob = () => import("@/views/dashboard/company/PostJobView.vue");
const DashboardCompanyJobs = () => import("@/views/dashboard/company/JobsView.vue");
const DashboardCompanyJobDescription = () => import("@/views/dashboard/company/JobDescriptionView.vue");
const DashboardCompanyProfile = () => import("@/views/dashboard/company/ProfileView.vue");
const DashboardCompanyManageRecruiters = () => import("@/views/dashboard/company/ManageRecruitersView.vue");
const DashboardCompanyChat = () => import("@/views/dashboard/company/ChatView.vue");
const DashboardCompanySettings = () => import("@/views/dashboard/company/SettingView.vue");

const CreateProfileView = () => import("@/views/dashboard/person/CreateProfileView.vue");
const DashboardApplicantMyCurriculoList = () => import("@/views/dashboard/applicant/MyCurriculoListView.vue");
const DashboardApplicantPreviewMyCurriculo = () => import("@/views/dashboard/applicant/preview/CurriculoView.vue");
const DashboardApplicantCurriculumPreview = () => import("@/views/dashboard/applicant/preview/CurriculumPreviewView.vue");
const DashboardApplicantJobs = () => import("@/views/dashboard/applicant/JobsView.vue");
const CandidateDashboardView = () => import("@/views/dashboard/applicant/CandidateDashboardView.vue");
const DashboardApplicantJobDescription = () => import("@/views/dashboard/applicant/JobDescriptionView.vue");
const DashboardApplicantProfile = () => import("@/views/dashboard/applicant/ProfileView.vue");
const DashboardApplicantChat = () => import("@/views/dashboard/applicant/ChatView.vue");
const DashboardApplicantSettings = () => import("@/views/dashboard/applicant/setting/SettingView.vue");
const DashboardApplicantInterviews = () => import("@/views/dashboard/applicant/InterviewsView.vue");

const DashboardRecruiter = () => import("@/views/dashboard/recruiter/IndexView.vue");
const DashboardRecruiterPostjob = () => import("@/views/dashboard/recruiter/PostJobView.vue");
const DashboardRecruiterJobs = () => import("@/views/dashboard/recruiter/JobsView.vue");
const DashboardRecruiterJobDescription = () => import("@/views/dashboard/company/JobDescriptionView.vue");
const DashboardRecruiterProfile = () => import("@/views/dashboard/recruiter/ProfileView.vue");
const DashboardRecruiterChat = () => import("@/views/dashboard/recruiter/ChatView.vue");
const DashboardRecruiterSettings = () => import("@/views/dashboard/recruiter/SettingView.vue");
const DashboardRecruiterManageCompanies = () => import("@/views/dashboard/recruiter/ManageCompaniesView.vue");
const IndexView = () => import("@/views/dashboard/person/IndexView.vue");
const NotificationsView = () => import("@/views/dashboard/NotificationsView.vue");
const ReferralsView = () => import("@/views/dashboard/ReferralsView.vue");
const SubscriptionView = () => import("@/views/dashboard/SubscriptionView.vue");
const ModerationReportsView = () => import("@/views/dashboard/ModerationReportsView.vue");
const ApplicationFunnelView = () => import("@/views/dashboard/ApplicationFunnelView.vue");

import store from "@/store";
import { translate } from "@/composables/translate";
import authUser from "@/composables/authUser";
import ipHandler from "@/composables/ipHandler"; 
import { applyRouteSeo } from "@/composables/seoMeta";

const routes = [
  {
    path: "/",
    name: "public",
    component: DefaultLayout,
    redirect: "/",
    children: [
      { path: ":userType/:name/referrals", component: ReferralsView },
      {
        path: "/",
        name: "home",
        component: Index,
        meta: { seo: {
          title: "Jobifull — Empregos internacionais e talentos sem fronteiras",
          description: "Encontre vagas internacionais, conecte-se com empresas e desenvolva sua carreira sem fronteiras com a Jobifull."
        } },
      },
      {
        path: "/login",
        component: Login,
      },
      {
        path: "/register",
        component: Register,
      },
      {
        path: "/plans",
        component: Plans,
      },
      {
        path: "/applicants",
        component: Applicants,
      },
      {
        path: "/applicants/:name",
        component: Applicant,
      },
      {
        path: "/companies",
        component: Companies,
        meta: { seo: {
          title: "Empresas que contratam talentos | Jobifull",
          description: "Conheça empresas, explore seus perfis e encontre novas oportunidades profissionais na Jobifull."
        } },
      },
      {
        path: "/companies/:slugCompany",
        component: CompanyPublicProfile,
        meta: { seo: {
          title: "Perfil da empresa | Jobifull",
          description: "Conheça a empresa, sua atuação e suas oportunidades profissionais na Jobifull.",
          type: "profile"
        } },
      },
      {
        path: "/jobs",
        component: Jobs,
        meta: { seo: {
          title: "Vagas internacionais | Jobifull",
          description: "Pesquise vagas, filtre oportunidades e encontre seu próximo trabalho no Brasil ou no exterior com a Jobifull."
        } },
      },
      {
        path: "/reset-password/:token",
        component: ResetPassword,
      },
      {
        path: "/about",
        component: About,
      },
      {
        path: "/faq",
        component: Faq,
      },
      {
        path: "/privacy-policy",
        component: PrivacyPolicy,
      },
      {
        path: "/terms",
        component: Terms,
      }, 
      {
        path: "/status",
        name: "status-support",
        component: StatusSupport,
        meta: { seo: {
          title: "Status e suporte | Jobifull",
          description: "Consulte a disponibilidade dos serviços Jobifull e encontre o canal oficial de suporte."
        } },
      },
      {
        path: "/compatibility",
        name: "compatibility",
        component: Compatibility,
        meta: { seo: {
          title: "Compatibilidade | Jobifull",
          description: "Consulte os navegadores, dispositivos e versões oficialmente suportados pela Jobifull."
        } },
      },
      {
        path: "/reset-password",
        component: ResetPassword,
      },
      {
        path: "/job/:job_id",
        component: JobDetailPageComponent,
        meta: { seo: {
          title: "Vaga de emprego | Jobifull",
          description: "Confira os detalhes desta oportunidade e candidate-se pela Jobifull.",
          type: "article"
        } },
      },
      {
        path: "/job/:job_id/:job_slug",
        component: JobDetailPageComponent,
        meta: { seo: {
          title: "Vaga de emprego | Jobifull",
          description: "Confira os detalhes desta oportunidade e candidate-se pela Jobifull.",
          type: "article"
        } },
      },

    ],
  },
  // ########### Rotas Dashboard da EMPRESA, PROFISSIONAL E RECRUTADOR ###########
  {
    path: "/dashboard",
    component: DashboardLayout,
    redirect: "/",
    children: [
      // ########### Rotas empresa ###########
      {
        path: "/dashboard/company",
        name: "company",
        redirect: "/",
        children: [
          {
            path: ":name/manage-recruiters",
            component: DashboardCompanyManageRecruiters,
          },
          {
            path: ":name",
            component: DashboardCompany,
          },
          {
            path: ":name/jobs",
            component: DashboardCompanyJobs,
          },
          {
            path: ":name/post-job",
            component: DashboardCompanyPostjob,
          },
          {
            path: ":name/post-job/:job_id",
            component: DashboardCompanyPostjob,
          },
          {
            path: ":name/jobs/:job_id",
            component: DashboardCompanyJobDescription,
          },
          {
            path: ":name/profile",
            component: DashboardCompanyProfile,
          },
          {
            path: ":name/settings",
            component: DashboardCompanySettings,
          },
          {
            path: ":name/messages",
            component: DashboardCompanyChat,
          },
          {
            path: ":name/notifications",
            component: NotificationsView,
          },
          { path: ":name/subscription", component: SubscriptionView },
          { path: ":name/reports", component: ModerationReportsView },
          { path: ":name/analytics", component: ApplicationFunnelView },
        ],
      },

      // ########### Rotas profissional ###########
      {
        path: "/dashboard/professional",
        name: "professional",
        children: [
          {
            path: ":name",
            component: CandidateDashboardView,
          },
          {
            path: ":name/jobs-application",
            component: DashboardApplicantJobs,
          },
          {
            path: ":name/curriculo-list",
            component: DashboardApplicantMyCurriculoList,
          },
          {
            path: ":name/curriculum",
            component: DashboardApplicantPreviewMyCurriculo,
          },
          {
            path: ":name/preview/curriculum",
            component: DashboardApplicantPreviewMyCurriculo,
          },
          {
            path: ":name/preview/curriculum/:curriculumId",
            component: DashboardApplicantCurriculumPreview,
          },
          {
            path: ":name/jobs",
            component: DashboardApplicantJobs,
          },
          {
            path: ":name/jobs/:description",
            component: DashboardApplicantJobDescription,
          },
          {
            path: ":name/profile",
            component: DashboardApplicantProfile,
          },
          {
            path: ":name/settings",
            component: DashboardApplicantSettings,
          },
          {
            path: ":name/messages",
            component: DashboardApplicantChat,
          },
          {
            path: ":name/notifications",
            component: NotificationsView,
          },
          {
            path: ":name/interviews",
            component: DashboardApplicantInterviews,
          },
          { path: ":name/subscription", component: SubscriptionView },
          { path: ":name/reports", component: ModerationReportsView },
        ],
      },

      // ########### Rotas Recrutador ###########
      {
        path: "/dashboard/recruiter",
        name: "recruiter",
        redirect: "/",
        children: [
          {
            path: ":name",
            component: DashboardRecruiter,
          },
          {
            path: ":name/post-job",
            component: DashboardRecruiterPostjob,
          },
          {
            path: ":name/jobs-application",
            component: DashboardRecruiterJobs,
          }, 
          {
            path: ":name/jobs",
            component: DashboardRecruiterJobs,
          },
          {
            path: ":name/jobs/:description",
            component: DashboardRecruiterJobDescription,
          },
          {
            path: ":name/manage-companies",
            component: DashboardRecruiterManageCompanies,
          },
          {
            path: ":name/profile",
            component: DashboardRecruiterProfile,
          },
          {
            path: ":name/settings",
            component: DashboardRecruiterSettings,
          },
          {
            path: ":name/messages", 
            component: DashboardRecruiterChat,
          },
          {
            path: ":name/notifications",
            component: NotificationsView,
          },
          { path: ":name/subscription", component: SubscriptionView },
          { path: ":name/reports", component: ModerationReportsView },
          { path: ":name/analytics", component: ApplicationFunnelView },
        ],
      }, 

      // ########### Rotas person ###########
      {
        path: "/dashboard/profile",
        component: IndexView,
      },
      {
        path: "/dashboard/notifications",
        component: NotificationsView,
      },
      {
        path: "/dashboard/:name/createprofile",
        component: CreateProfileView,
      }
    ], 
       
  },
  {
    path: "/maintance",
    name: "Maintance",
    component: Maintance,
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFound,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.afterEach((to) => {
  applyRouteSeo(to);
});

router.beforeEach((to, from, next) => {
  store.dispatch('setRouteChanged', {url: to.path});
  next();
});

/**
 * Middleware to check auth routes
 */
router.beforeEach(async (to, from, next) => {
  ipHandler.detectIp();

  if(store.getters.getToken)
    await authUser.restoreSession();

  if (to.path.startsWith('/dashboard')) {
    if (store.state.user.userLogged) {
      next();
    } else {
      if(!store.getters.isTryingToLogin){
        store.dispatch('saveSessionMessage', {message: translate('not authenticated'), messageType: 'error'});
      }
      next({path: '/login', replace: true});
    }
  } else {
    next();
  }
});

export default router;
